import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Download, FileText, Loader2 } from "lucide-react";

import { readStorage, type StorageFile, type StorageVolume } from "@/lib/api";
import { isStatus } from "@/lib/errors";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

// A reference to a file on one of the agent's workspace volumes.
export interface WorkspaceFileRef {
  volume: StorageVolume;
  path: string; // relative to the volume, e.g. "files/competitor-analysis.md"
  name: string;
}

// FileLinkContext carries the "open this file" callback down to the markdown
// renderer, so a path the agent mentions in chat becomes clickable.
export const FileLinkContext = createContext<((f: WorkspaceFileRef) => void) | null>(null);

const VOLUMES: StorageVolume[] = ["state", "skills", "playbook"];

// parseVolumePath recognizes a workspace file path the agent might mention (e.g.
// "state/files/report.md", "files/report.md", "skills/x.ts") and splits it into a
// {volume, path}. Returns null for anything that isn't a recognized file path, so
// ordinary code/text is left alone. Must end in an extension (a file, not a dir).
export function parseVolumePath(raw: string): WorkspaceFileRef | null {
  let s = (raw || "").trim().replace(/^[`'"]+|[`'"]+$/g, "").replace(/^\.?\//, "");
  if (!s || /\s/.test(s) || s.includes("..") || s.includes("\x00")) return null;
  if (!/\.[A-Za-z0-9]{1,8}$/.test(s)) return null; // looks like a file, not a bare word/dir
  const head = s.split("/")[0];
  let volume: StorageVolume;
  let path: string;
  if (VOLUMES.includes(head as StorageVolume)) {
    volume = head as StorageVolume;
    path = s.slice(head.length + 1);
  } else if (head === "files" || head === "memory" || head === "servers") {
    volume = "state"; // these subtrees live on the state volume
    path = s;
  } else {
    return null;
  }
  if (!path) return null;
  return { volume, path, name: path.split("/").pop() || path };
}

// PATH_RE finds candidate workspace paths in plain prose (validated by
// parseVolumePath before linkifying), so a path in a bullet/sentence — not just in
// backticks — becomes clickable. Group 1 is a left boundary (start-of-string or a
// non-path char) so the volume token can't match mid-word ("mystate/x.md") or as a
// non-leading segment ("config/state/x.md"); group 2 is the path to linkify.
// Trailing (?!\w) so an over-long dotted suffix (e.g. "backup.20260623120000")
// fails to match rather than being silently truncated to a wrong 8-char "ext".
const PATH_RE = /(^|[^\w./-])((?:state|skills|playbook|files|memory|servers)\/[\w./-]*\.[A-Za-z0-9]{1,8})(?!\w)/g;

// remarkWorkspaceFiles linkifies bare workspace paths in text nodes by rewriting
// them into mdast link nodes (url = the path). The chat's `a` renderer then turns
// those into clickable file chips. Skips text already inside links/code.
export function remarkWorkspaceFiles() {
  return (tree: unknown) => walkMdast(tree as MdastNode);
}

interface MdastNode {
  type: string;
  value?: string;
  url?: string;
  children?: MdastNode[];
}

const SKIP_PARENTS = new Set(["link", "inlineCode", "code"]);

function walkMdast(node: MdastNode) {
  if (!node || !Array.isArray(node.children)) return;
  const out: MdastNode[] = [];
  for (const child of node.children) {
    if (child.type === "text" && typeof child.value === "string") {
      out.push(...splitWorkspacePaths(child.value));
    } else {
      if (!SKIP_PARENTS.has(child.type)) walkMdast(child);
      out.push(child);
    }
  }
  node.children = out;
}

function splitWorkspacePaths(value: string): MdastNode[] {
  const nodes: MdastNode[] = [];
  let last = 0;
  PATH_RE.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = PATH_RE.exec(value)) !== null) {
    const path = m[2];
    const start = m.index + m[1].length; // skip the boundary char captured in group 1
    if (!parseVolumePath(path)) continue; // only linkify real workspace paths
    if (start > last) nodes.push({ type: "text", value: value.slice(last, start) });
    nodes.push({ type: "link", url: path, children: [{ type: "text", value: path }] });
    last = start + path.length;
  }
  if (nodes.length === 0) return [{ type: "text", value }];
  if (last < value.length) nodes.push({ type: "text", value: value.slice(last) });
  return nodes;
}

// FileLink renders a clickable file reference inside chat markdown. Falls back to
// plain children when there's no open handler in context (e.g. outside the chat).
export function FileLink({ file, children }: { file: WorkspaceFileRef; children: ReactNode }) {
  const open = useContext(FileLinkContext);
  if (!open) return <>{children}</>;
  return (
    <button
      type="button"
      onClick={() => open(file)}
      className="inline-flex items-center gap-1 rounded bg-primary/10 px-1.5 py-0.5 align-baseline font-mono text-[0.85em] text-primary hover:bg-primary/20"
      title={`Open ${file.name}`}
    >
      <FileText className="h-3 w-3 shrink-0" />
      {children}
    </button>
  );
}

// FileCard is a clickable attachment card for a non-image file the agent produced
// (e.g. a mission deliverable). Click opens the inline viewer.
export function FileCard({ file }: { file: WorkspaceFileRef }) {
  const open = useContext(FileLinkContext);
  return (
    <button
      type="button"
      onClick={() => open?.(file)}
      className="flex w-full max-w-sm items-center gap-2.5 rounded-lg border bg-card px-3 py-2.5 text-left transition hover:border-primary/40 hover:bg-primary/5"
      title={`Open ${file.name}`}
    >
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
        <FileText className="h-4 w-4" />
      </span>
      <span className="min-w-0">
        <span className="block truncate text-sm font-medium">{file.name}</span>
        <span className="block text-xs text-muted-foreground">Click to open</span>
      </span>
    </button>
  );
}

const IMAGE_EXT = new Set(["png", "jpg", "jpeg", "gif", "webp", "svg", "bmp", "ico"]);
const TEXT_EXT = new Set([
  "md", "markdown", "txt", "text", "csv", "tsv", "json", "jsonl", "yaml", "yml", "log",
  "ts", "tsx", "js", "jsx", "py", "go", "rs", "java", "rb", "php", "sh", "bash", "sql",
  "html", "xml", "toml", "ini", "env", "css", "c", "cpp", "h",
]);

const extMime = (name: string): string => {
  const e = name.split(".").pop()?.toLowerCase() || "";
  if (e === "png") return "image/png";
  if (e === "jpg" || e === "jpeg") return "image/jpeg";
  if (e === "gif") return "image/gif";
  if (e === "webp") return "image/webp";
  if (e === "svg") return "image/svg+xml";
  if (e === "json" || e === "jsonl") return "application/json";
  if (e === "csv") return "text/csv";
  if (TEXT_EXT.has(e)) return "text/plain";
  return "application/octet-stream";
};

// FilePreviewBody fetches a workspace file (authenticated, RBAC-gated) and renders
// it — markdown rendered, text in a code block, images shown, anything else offered
// as a download. Container-agnostic: hosted in a modal (FilePreviewDialog) or the
// chat's right-side viewer panel.
export function FilePreviewBody({ projectId, file }: { projectId: string; file: WorkspaceFileRef }) {
  const [state, setState] = useState<"loading" | "ok" | "error">("loading");
  const [sf, setSf] = useState<StorageFile | null>(null);
  const [denied, setDenied] = useState(false);

  useEffect(() => {
    let live = true;
    setState("loading");
    readStorage(projectId, file.volume, file.path)
      .then((f) => {
        if (live) {
          setSf(f);
          setState("ok");
        }
      })
      .catch((e) => {
        if (live) {
          setDenied(isStatus(e, 403));
          setState("error");
        }
      });
    return () => {
      live = false;
    };
  }, [projectId, file.volume, file.path]);

  const ext = file.name.split(".").pop()?.toLowerCase() || "";
  const text = useMemo(() => {
    if (!sf || (!TEXT_EXT.has(ext) && ext !== "svg")) return "";
    try {
      return new TextDecoder().decode(Uint8Array.from(atob(sf.content_b64), (c) => c.charCodeAt(0)));
    } catch {
      return "";
    }
  }, [sf, ext]);
  const dataUri = sf ? `data:${extMime(file.name)};base64,${sf.content_b64}` : "";

  return (
    <div>
      {state === "loading" && (
        <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      )}
      {state === "error" && (
        <div className="py-8 text-sm text-muted-foreground">
          {denied
            ? "You don't have permission to view this file (editor access is required)."
            : "Couldn't open this file. It may not exist yet, or the agent is asleep."}
        </div>
      )}
      {state === "ok" && sf && (
        <>
          <div className="mb-2 flex items-center gap-2">
            {sf.truncated && <p className="text-xs text-warning">Showing the first part of a large file.</p>}
            <a
              href={dataUri}
              download={file.name}
              className="ml-auto inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs text-muted-foreground hover:text-foreground"
            >
              <Download className="h-3.5 w-3.5" /> Download
            </a>
          </div>
          {IMAGE_EXT.has(ext) && ext !== "svg" ? (
            <img src={dataUri} alt={file.name} className="max-w-full rounded-lg border" />
          ) : ext === "md" || ext === "markdown" ? (
            <div className="space-y-2 text-sm leading-relaxed [&_h1]:mb-2 [&_h1]:mt-4 [&_h1]:text-lg [&_h1]:font-semibold [&_h2]:mb-2 [&_h2]:mt-4 [&_h2]:text-base [&_h2]:font-semibold [&_h3]:mt-3 [&_h3]:font-semibold [&_li]:ml-5 [&_li]:list-disc [&_pre]:overflow-auto [&_pre]:rounded [&_pre]:bg-muted/60 [&_pre]:p-2 [&_table]:w-full [&_td]:border [&_td]:px-2 [&_td]:py-1 [&_th]:border [&_th]:px-2 [&_th]:py-1">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{text}</ReactMarkdown>
            </div>
          ) : TEXT_EXT.has(ext) ? (
            <pre className="overflow-auto rounded-lg border bg-muted/40 p-3 font-mono text-xs leading-relaxed">{text}</pre>
          ) : (
            <div className="py-8 text-center text-sm text-muted-foreground">
              This file type can't be previewed.{" "}
              <a href={dataUri} download={file.name} className="text-primary underline underline-offset-2">
                Download it
              </a>{" "}
              instead.
            </div>
          )}
        </>
      )}
    </div>
  );
}

// FilePreviewDialog hosts FilePreviewBody in a centered modal (used outside the
// chat, where there's no side panel).
export function FilePreviewDialog({
  projectId,
  file,
  onClose,
}: {
  projectId: string;
  file: WorkspaceFileRef;
  onClose: () => void;
}) {
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[85vh] max-w-3xl overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
            <span className="truncate font-mono text-sm">{file.name}</span>
          </DialogTitle>
        </DialogHeader>
        <div className="overflow-auto" style={{ maxHeight: "calc(85vh - 5rem)" }}>
          <FilePreviewBody projectId={projectId} file={file} />
        </div>
      </DialogContent>
    </Dialog>
  );
}
